import { useState, useEffect } from "react";
import type { Diary, DbDiary } from "../types/diary";
import type { RealtimePostgresChangesPayload } from "@supabase/supabase-js";
import { diaryService, convertToAppDiary } from "@/services/diaryService";

export const useDiary = (diaryId: string | undefined) => {
	const [diary, setDiary] = useState<Diary | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<Error | null>(null);

	useEffect(() => {
		if (!diaryId) {
			setLoading(false);
			return;
		}

		const fetchDiary = async () => {
			try {
				const data = await diaryService.getDiaryById(diaryId);
				setDiary(data);
			} catch (err) {
				setError(err as Error);
			} finally {
				setLoading(false);
			}
		};

		fetchDiary();

		// リアルタイム更新のサブスクリプション
		const subscription = diaryService.subscribeToChanges(
			(payload: RealtimePostgresChangesPayload<DbDiary>) => {
				if (payload.eventType === "UPDATE" && payload.new.id === diaryId) {
					setDiary(convertToAppDiary(payload.new));
				}
			},
		);

		return () => {
			subscription.unsubscribe();
		};
	}, [diaryId]);

	const updateDiary = async (
		diary: Partial<
			Omit<Diary, "id" | "userId" | "createdAt" | "updatedAt" | "user">
		>,
	) => {
		if (!diaryId) return;
		try {
			const updatedDiary = await diaryService.updateDiary(diaryId, diary);
			return updatedDiary;
		} catch (err) {
			setError(err as Error);
			throw err;
		}
	};

	const deleteDiary = async () => {
		if (!diaryId) return;
		try {
			await diaryService.deleteDiary(diaryId);
		} catch (err) {
			setError(err as Error);
			throw err;
		}
	};

	return {
		diary,
		loading,
		error,
		updateDiary,
		deleteDiary,
	};
};
